import {
  Box,
  Button,
  ColumnDatas,
  getColor,
  Modal,
  Table,
  Typography,
  useToast,
} from "fds";
import { useTranslation } from "react-i18next";
import useAdminDeptStore from "../../redux/dispatcher/useAdminDeptStore";
import { useEffect, useState } from "react";
import { nanoid } from "@reduxjs/toolkit";
import utilsCommon from "../../shared/utils/common";
import { Dept, Position } from "../../types/adminDept";

export interface AddDepartment {
  id: string | undefined;
  name: string | undefined;
  email: string | undefined;
  dept: { code: string | undefined; name: string | undefined };
  position: {
    code: string | undefined;
    name: string | undefined;
  };
  role: { name: string | undefined };
  phoneNumber: string | undefined;
}

export type ModalProps = {
  showExcelModal: boolean;
  toggleExcelModal: (open: boolean) => void;
  selectedDeptList: AddDepartment[] | undefined;
  deptState: Dept[];
  positionState: Position[];
  fileSelected: any;
};

const AddExcelGroupModal: React.FC<ModalProps> = ({
  showExcelModal,
  toggleExcelModal,
  selectedDeptList,
  deptState,
  positionState,
  fileSelected,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { uploadDeptFile } = useAdminDeptStore();
  const [userIds, setUserIds] = useState([] as string[]);
  const [countSuccess, setCountSuccess] = useState(0);
  const [confirmAdd, setConfirmAdd] = useState(false);

  useEffect(() => {
    if (selectedDeptList) {
      let tempUserIds: string[] = [];
      let tempCountSuccess = selectedDeptList.length;

      selectedDeptList.map((item) =>
        tempUserIds.push(item.id !== undefined ? item.id : "")
      );
      setUserIds(tempUserIds);

      selectedDeptList.map(
        (item: AddDepartment) =>
          !checkValidate(item, tempUserIds, deptState, positionState) &&
          tempCountSuccess--
      );

      setCountSuccess(tempCountSuccess);
    }
  }, []);

  const checkDuplicate = (id: string, userLists: string[]) => {
    if (userLists.length > 1)
      return userLists.filter((item) => item === id).length === 1;
    else return true;
  };

  const checkValidate = (
    row: AddDepartment,
    userLists: string[],
    deptList: Dept[],
    positionList: Position[]
  ) => {
    return (
      row.name !== null &&
      utilsCommon.maxLengthValidation(row.name ?? "", 50) &&
      row.id !== null &&
      utilsCommon.userIdValidation(row.id ?? "") &&
      utilsCommon.maxLengthValidation(row.id ?? "", 50) &&
      checkDuplicate(row.id ?? "", userLists) &&
      row.email !== null &&
      utilsCommon.emailValidation(row.email ?? "") &&
      row.position.code !== null &&
      positionList.filter((item: any) => item.code === row.position.code)
        .length !== 0 &&
      row.dept.code !== null &&
      deptList.filter((item: any) => item.code === row.dept.code).length !==
        0 &&
      (row.phoneNumber !== null ||
        utilsCommon.phoneNumberValidation(row.phoneNumber))
    );
  };

  const getPositionOrDeptName = (code: string, list: any) => {
    return list.filter((item: any) => item.code === code).length !== 0 ? (
      <span>{list.filter((item: any) => item.code === code)[0].name}</span>
    ) : (
      ""
    );
  };

  const registerExcelFile = async () => {
    const result: any = await uploadDeptFile(fileSelected);
    if (
      result.payload.entity !== null &&
      result.payload.entity.code === "success"
    ) {
      toast.toastMsg(nanoid(), t("admin.common.alert.add"), "success");
      //등록 완료되면 모달창 닫히기
      setConfirmAdd(false);
      toggleExcelModal(false);
    } else if (result.error.message) {
      toast.toastMsg(nanoid(), result.error.message, "error");
    } else {
      toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
    }
  };

  function transformTableData(data: any) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data.length > 0 &&
      data.map((row: AddDepartment) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: "",
            },
            {
              columnText:
                row.name === null || row.name === "" ? (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : utilsCommon.maxLengthValidation(row.name + "", 50) ? (
                  <Typography ellipsis>{row.name}</Typography>
                ) : (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {row.name}
                  </Typography>
                ),
            },
            {
              columnText:
                row.id === null || row.id === "" ? (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : utilsCommon.userIdValidation(row.id ?? "") &&
                  utilsCommon.maxLengthValidation(row.id + "", 50) &&
                  userIds &&
                  userIds.filter((item) => item === row.id).length === 1 ? (
                  <Typography ellipsis>{row.id}</Typography>
                ) : (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {row.id}
                  </Typography>
                ),
            },
            {
              columnText:
                row.email === null || row.email === "" ? (
                  <Typography exactColor={getColor("COLOR_TEXT_ERROR")}>
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : utilsCommon.emailValidation(row.email + "") ? (
                  <Typography>{row.email}</Typography>
                ) : (
                  <Typography exactColor={getColor("COLOR_TEXT_ERROR")}>
                    {row.email}
                  </Typography>
                ),
            },
            {
              columnText:
                row.dept.code === null || row.dept.code === "" ? (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : getPositionOrDeptName(row.dept.code + "", deptState) !==
                  "" ? (
                  <Typography ellipsis>{row.dept.code}</Typography>
                ) : (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {row.dept.code}
                  </Typography>
                ),
            },
            {
              columnText:
                row.dept.code === null || row.dept.code === "" ? (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : getPositionOrDeptName(row.dept.code + "", deptState) !==
                  "" ? (
                  <Typography ellipsis>
                    {getPositionOrDeptName(row.dept.code + "", deptState)}
                  </Typography>
                ) : (
                  ""
                ),
            },
            {
              columnText:
                row.position.code === null || row.position.code === "" ? (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : getPositionOrDeptName(
                    row.position.code + "",
                    positionState
                  ) !== "" ? (
                  <Typography ellipsis>{row.position.code}</Typography>
                ) : (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {row.position.code}
                  </Typography>
                ),
            },
            {
              columnText:
                row.position.code === null || row.position.code === "" ? (
                  <Typography
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("admin.common.label.noInput")}
                  </Typography>
                ) : getPositionOrDeptName(
                    row.position.code + "",
                    positionState
                  ) !== "" ? (
                  <Typography ellipsis>
                    {getPositionOrDeptName(
                      row.position.code + "",
                      positionState
                    )}
                  </Typography>
                ) : (
                  ""
                ),
            },
            {
              columnText: utilsCommon.phoneNumberValidation(
                row.phoneNumber + ""
              ) ? (
                <Typography ellipsis>{row.phoneNumber}</Typography>
              ) : (
                <Typography ellipsis exactColor={getColor("COLOR_TEXT_ERROR")}>
                  {row.phoneNumber}
                </Typography>
              ),
            },
          ],
          id: row.id + "",
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  return (
    <>
      {/* 선택한 파일에 대한 표를 나타낸 모달창 */}
      <Modal
        open={showExcelModal}
        onClose={() => toggleExcelModal(false)}
        footer={
          <Box justifyContent={"flex-end"}>
            <Button
              btnStyle="fill"
              label={t("admin.common.button.cancel")}
              type="secondary"
              onClick={() => {
                toggleExcelModal(false);
                setCountSuccess(0);
              }}
            />
            <Button
              btnStyle="fill"
              label={t("admin.common.button.ok")}
              classes="mg ml-8"
              onClick={() => setConfirmAdd(true)}
              state={
                userIds.length > countSuccess ||
                userIds.length >= 300 ||
                countSuccess === 0
                  ? "disable"
                  : "default"
              }
            />
          </Box>
        }
        title={t("admin.dept.uploadDept")}
        width={1000}
        subTitle={t("admin.dept.deptLength", {
          count: selectedDeptList?.length,
        })}
        body={
          <Box>
            <Box width={950} direction="column">
              <Table
                classes="mg mt-8"
                width={"100%"}
                height={(selectedDeptList ? 500 : 0) + "px"}
                header={[
                  {
                    headerText: "",
                    width: "20px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.name"),
                    width: "120px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.userId"),
                    width: "100px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.emailAddr"),
                    width: "130px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.deptCode"),
                    width: "260px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.deptName"),
                    width: "100px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.jobCode"),
                    width: "80px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.jobName"),
                    width: "80px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.phone"),
                    width: "80px",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformTableData(selectedDeptList)}
                totalCount={selectedDeptList ? selectedDeptList.length : 0}
                endReached={() => {}}
                columnHeight="md"
              />
            </Box>
          </Box>
        }
      />

      {/* 등록 시 확인 모달창 */}
      <Modal
        title={t("admin.dept.uploadDept")}
        width={528}
        onClose={() => setConfirmAdd(false)}
        open={confirmAdd}
        footer={
          <Box justifyContent={"flex-end"}>
            <Button
              type="secondary"
              btnStyle="fill"
              label={t("admin.common.button.cancel")}
              onClick={() => setConfirmAdd(false)}
            />
            <Button
              btnStyle="fill"
              label={t("admin.common.label.register")}
              classes="mg ml-8"
              onClick={() => registerExcelFile()}
            />
          </Box>
        }
        body={
          <Box>
            <Typography classes={"mg ml-4"}>{fileSelected.name}</Typography>
            <Typography classes={"mg ml-4"}>
              {t("admin.dept.confirmAddExcelFile")}
            </Typography>
          </Box>
        }
      />
    </>
  );
};
export default AddExcelGroupModal;
